<script setup lang="ts">
  import DataViewer from '@/components/DataViewer.vue'
  import { GetFileDownloadUrlArgs } from '@/components/FileManager/models/GetFileDownloadUrlArgs'
  import {
    IAplosFileApiService
  } from '@/components/FileManager/services/AplosFileApiService'
  import FullScreenModal from '@/components/FullScreenModal.vue'
  import Button from '@/components/ui/button/Button.vue'
  import DropdownMenu from '@/components/ui/dropdown-menu/DropdownMenu.vue'
  import DropdownMenuContent from '@/components/ui/dropdown-menu/DropdownMenuContent.vue'
  import DropdownMenuItem from '@/components/ui/dropdown-menu/DropdownMenuItem.vue'
  import DropdownMenuTrigger from '@/components/ui/dropdown-menu/DropdownMenuTrigger.vue'
  import { useUser } from '@/composables/useUser'
  import { Execution } from '@/shared/Executions/models/Execution'
  import { downloadFile, extractFilename } from '@/utils/download'
  import { extractApiErrorMessage } from '@/utils'
  import { FileSearchOutlined } from '@ant-design/icons-vue'
  import { DownloadIcon, EllipsisIcon, FileSearch, Loader2 } from 'lucide-vue-next'
  import { ref } from 'vue'
  import { useRouter } from 'vue-router'
  import { toast } from 'vue-sonner'

  const props = defineProps<{
    record: Execution
    index?: number
    selectable?: boolean
    onCellClick?: (record: Execution, e?: Event) => void
    aplosFileApiService: IAplosFileApiService
  }>()

  const router = useRouter()
  const auth = useUser()

  const selectedExecution = ref<Execution | undefined>()
  const dataViewerFileId = ref<string | undefined>()
  const dataViewerModalOpen = ref(false)
  const downloadingFileExecutionId = ref<string | undefined>()

  const onViewFileDataClicked = (record: Execution) => {
    selectedExecution.value = record
    dataViewerFileId.value = record?.input?.fileId
    dataViewerModalOpen.value = true
  }

  const closeDataViewerModal = () => {
    dataViewerModalOpen.value = false
    dataViewerFileId.value = undefined
  }

  const onDownloadDataClicked = async (record: Execution) => {
    await auth.initialize()
    if (!auth.user.tenant_id || !auth.user.id || !record?.input?.fileId) {
      toast.error('Missing user or file information.')
      return
    }
    downloadingFileExecutionId.value = record.id
    try {
      const args = new GetFileDownloadUrlArgs({ tenantId: auth.user.tenant_id, userId: auth.user.id, fileId: record?.input?.fileId })

      const response = await props.aplosFileApiService.getFileDownloadUrl(args)
      const downloadUrl = response.data.downloadUrl!

      await downloadFile(downloadUrl, extractFilename(downloadUrl))
    } catch (e: any) {
      toast.error(extractApiErrorMessage(e) || 'Failed to download file.')
    } finally {
      downloadingFileExecutionId.value = undefined
    }
  }

  const onFindFileClicked = (record: Execution) => {
    if (!record?.input?.fileId) return
    const resolved = router.resolve({
      path: '/app/uploads',
      query: { search: record?.input?.fileId }
    })
    const url = window.location.origin + resolved.href
    window.open(url, '_blank', 'noopener,noreferrer')
  }
</script>

<template>
  <div v-if="record.id == downloadingFileExecutionId">
    <Button variant="default" size="icon">
      <Loader2 class="size-5 animate-spin" />
    </Button>
  </div>
  <div v-else class="flex gap-1">
    <DropdownMenu>
      <DropdownMenuTrigger as-child>
        <Button variant="default" size="icon">
          <EllipsisIcon />
          <span class="sr-only">Actions</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" class="min-w-[200px]">
        <!-- View File Data -->
        <DropdownMenuItem
          :disabled="!record?.input?.fileId"
          @click="() => onViewFileDataClicked(record)"
          class="gap-2"
        >
          <FileSearchOutlined class="size-4" />
          <span>View File Data</span>
        </DropdownMenuItem>

        <!-- Download Dataset -->
        <DropdownMenuItem
          :disabled="!record?.input?.fileId"
          @click="() => onDownloadDataClicked(record)"
          class="gap-2"
        >
          <DownloadIcon class="size-4" />
          <span>Download Dataset</span>
        </DropdownMenuItem>

        <!-- Find File -->
        <DropdownMenuItem
          :disabled="!record?.input?.fileId"
          @click="() => onFindFileClicked(record)"
          class="gap-2"
        >
          <FileSearch class="size-4" />
          <span>Find File</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  </div>

  <FullScreenModal
    :open="dataViewerModalOpen"
    title="Data Viewer"
    :description="selectedExecution?.name"
    @close="closeDataViewerModal"
  >
    <DataViewer :file-id="dataViewerFileId" />
  </FullScreenModal>
</template>
